import { get } from 'lodash'

import { Colors, Services } from '@sigma-frontend-libs/commons'
import { MODERATION_VALUES } from '@sigma-legacy-libs/essentials/lib/constants'
import { SendingTypes } from '@sigmasms/shared-types'

import { components, icons, moderationColors, moderationIcons, prioritizeAccountInList } from '@/utils'

import info from '@/components/info'
import arrow from '@/components/misc/arrow'
import selectorOwner from '@/components/selector/owner'
import selectorSendingType from '@/components/selector/sendingType'
import requisites from '@/components/services/sendernames/form/requisites'
import tag from '@/components/tag'

function renderInfo(h) {
  if (this.isNew) {
    if (this.proxy.type === SendingTypes.sms || this.proxy.type === SendingTypes.voice) {
      return h(
        info,
        {
          props: {
            value: this.getTranslate(`${Services.sendernames}.warnings.name.${this.proxy.type}`),
            icon: icons.warning_amber,
            color: Colors.warning
          }
        }
      )
    }
  }
}
function renderIsActiveField(h) {
  if (this.canUpdateSendernamesAsAdmin) {
    return h(
      components.switch,
      {
        props: {
          value: this.proxy.isActive,
          tooltip: this.getTranslate(`${Services.sendernames}.labels.isActive`),
          outline: true
        },
        on: {
          input: event => {
            this.proxy.isActive = event
          }
        }
      }
    )
  }
}
function renderNameField(h) {
  let nameError
  if (this.proxy.name && !this.senderNameIsValid) {
    nameError = this.getTranslate(`${Services.sendernames}.errors.${this.proxy.type}`)
  }

  return h(
    components['text-field'],
    {
      props: {
        value: this.proxy.name,
        label: this.getTranslate(`${Services.sendernames}.labels.name`),
        error: nameError,
        disabled: !this.isNew && !this.canUpdateSendernamesAsAdmin,
        dense: true,
        required: true,
        autofocus: true,
        mask: this.senderNameMask
      },
      on: {
        input: event => {
          this.proxy.name = event
        }
      }
    }
  )
}
function renderNameAndIsActiveFields(h) {
  if (this.proxy.type) {
    return h(
      'div',
      {
        class: 'column',
        style: { 'grid-template-columns': '1fr auto' }
      },
      [
        renderNameField.call(this, h),
        renderIsActiveField.call(this, h)
      ]
    )
  }
}
function renderName(h) {
  return h(
    'div',
    {
      class: 'grid'
    },
    [
      renderInfo.call(this, h),
      renderNameAndIsActiveFields.call(this, h)
    ]
  )
}

function renderModerationField(h) {
  if (this.canUpdateSendernamesAsResellerUp) {
    return h(
      components.select,
      {
        props: {
          value: this.proxy.moderation,
          label: this.getTranslate(`${Services.sendernames}.labels.moderation`),
          items: MODERATION_VALUES.map(moderation => {
            return {
              title: this.getTranslate(`misc.moderation.${moderation}`),
              value: moderation,
              icon: moderationIcons[moderation],
              iconColor: moderationColors[moderation]
            }
          }),
          error: this.errors.moderation,
          dense: true,
          required: true,
          details: false
        },
        on: {
          input: event => {
            this.proxy.moderation = event
          }
        },
        scopedSlots: {
          selection: ({ item }) => {
            return h(
              tag,
              {
                props: {
                  label: this.getTranslate(`misc.moderation.${item.value}`),
                  color: moderationColors[item.value],
                  as: 'select-dense'
                }
              }
            )
          }
        }
      }
    )
  }
}

function renderTypeField(h) {
  return h(
    selectorSendingType,
    {
      props: {
        value: this.proxy.type,
        label: this.getTranslate(`${Services.sendernames}.labels.type`),
        permission: `advanced.${Services.sendernames}.get`,
        error: this.errors.type,
        required: true
      },
      on: {
        input: event => {
          this.proxy.type = event
        }
      }
    }
  )
}

/**
 * Рендерит поле комментария имени отправителя.
 *
 * Читает и записывает `proxy.meta.comment` без проверки на существование `proxy.meta`.
 * Объект `meta` приходит из пустой сущности `empty.sendernames.create` и попадает в
 * `createData.data` внутри `Service.init` синхронно, пока `inputFilter` шаблона
 * `src/components/services/sendernames/template/index.js` остаётся синхронным.
 * Асинхронный `inputFilter` откладывает это присваивание на микрозадачу: первый рендер
 * получает значение по умолчанию `{}` из миксина `proxy`, а обращение к `meta.comment`
 * бросает `TypeError: Cannot read properties of undefined (reading 'comment')`.
 *
 * @param {Function} h функция создания узла
 * @returns {object} узел поля комментария
 */
function renderCommentField(h) {
  let commentCount = 0
  if (this.proxy.meta.comment !== undefined) {
    commentCount = this.proxy.meta.comment.length
  }

  return h(
    components.textarea,
    {
      props: {
        value: this.proxy.meta.comment,
        label: this.getTranslate(`${Services.sendernames}.labels.meta.comment`),
        hint: this.getTranslate(`${Services.sendernames}.hints.meta.comment`),
        readonly: false, // https://sigmasms.slack.com/archives/C03RRLZB8MP/p1736413622572719
        count: commentCount,
        grow: true,
        dense: true,
        details: true,
        maxlength: 150,
        required: true
      },
      on: {
        input: event => {
          this.proxy.meta.comment = event
        }
      }
    }
  )
}

function renderWarning(h) {
  if (this.canCreateSendernamesAsMeOrReseller) {
    return h(
      info,
      {
        props: {
          value: this.getTranslate(`${Services.sendernames}.warnings.meta.comment`),
          icon: icons.warning_amber,
          color: Colors.warning
        }
      }
    )
  }
}

function renderExampleComment(h) {
  if (this.canCreateSendernamesAsMeOrReseller) {
    return h(
      info,
      {
        props: {
          value: this.getTranslate(`${Services.sendernames}.examples.meta.comment`),
          color: Colors.info
        }
      }
    )
  }
}

function renderOwnerField(h) {
  return h(
    selectorOwner,
    {
      props: {
        value: this.proxy.OwnerId,
        clearItems: prioritizeAccountInList
      },
      on: {
        input: event => {
          this.proxy.OwnerId = event
        }
      }
    }
  )
}

function renderAdvancedComment(h) {
  switch (true) {
    case this.canCreateSendernamesAsReseller: {
      return h(
        'div',
        {
          class: 'grid'
        },
        [
          h(
            components.textarea,
            {
              props: {
                value: this.proxy.meta.resellerComment,
                label: this.getTranslate(`${Services.sendernames}.labels.meta.resellerComment`),
                grow: true,
                details: false
              },
              on: {
                input: event => {
                  this.proxy.meta.resellerComment = event
                }
              }
            }
          ),

          h(
            components.textarea,
            {
              props: {
                value: this.proxy.meta.resellerCommentPrivate,
                label: this.getTranslate(`${Services.sendernames}.labels.meta.resellerCommentPrivate`),
                grow: true,
                details: false
              },
              on: {
                input: event => {
                  this.proxy.meta.resellerCommentPrivate = event
                }
              }
            }
          )
        ]
      )
    }
    case this.canCreateSendernamesAsAdmin: {
      return h(
        'div',
        {
          class: 'grid'
        },
        [
          h(
            components.textarea,
            {
              props: {
                value: this.proxy.meta.adminComment,
                label: this.getTranslate(`${Services.sendernames}.labels.meta.adminComment`),
                grow: true,
                details: false
              },
              on: {
                input: event => {
                  this.proxy.meta.adminComment = event
                }
              }
            }
          ),

          h(
            components.textarea,
            {
              props: {
                value: this.proxy.meta.adminCommentPrivate,
                label: this.getTranslate(`${Services.sendernames}.labels.meta.adminCommentPrivate`),
                grow: true,
                details: false
              },
              on: {
                input: event => {
                  this.proxy.meta.adminCommentPrivate = event
                }
              }
            }
          )
        ]
      )
    }
  }
}

function renderAdditional(h) {
  if (this.canCreateSendernamesAsResellerUp) {
    return h(
      components['expansion-panels'],
      {
        props: {
          outline: true,
          rounded: true
        }
      },
      [
        h(
          components['expansion-panel'],
          {
            scopedSlots: {
              header: ({ expanded }) => {
                return h(
                  'div',
                  { class: 'default-expansion-panel-header' },
                  [
                    this.getTranslate('misc.additional'),
                    h(arrow, { props: { expanded } })
                  ]
                )
              },
              default: () => {
                return h(
                  'div',
                  { class: 'grid pa-3' },
                  [
                    renderOwnerField.call(this, h),
                    renderAdvancedComment.call(this, h)
                  ]
                )
              }
            }
          }
        )
      ]
    )
  }
}

function renderRequisites(h) {
  if (!this.isNew) {
    if (this.canGetRequisitesAsResellerUp) {
      return h(
        requisites,
        {
          props: {
            OwnerId: this.proxy.Owner && this.proxy.Owner.id || this.proxy.OwnerId,
            foreign: this.proxy.$foreign
          }
        }
      )
    }
  }
}

function renderComment(h, comment, { value, color }) {
  return h(
    info,
    {
      props: {
        value: comment,
        icon: value,
        color
      }
    }
  )
}

function renderCommentFromAdmin(h) {
  if (this.canGetSendernames) {
    const comment = get(this.value, 'meta.adminComment') || get(this.value, 'meta.resellerComment')
    if (comment) {
      return renderComment.call(this, h, comment, {
        value: icons.error_outline,
        color: Colors.error
      })
    }
  }
}

export default function(h) {
  return h(
    components.form,
    {
      class: {
        [`${Services.sendernames}-form`]: true,
        [`${Services.sendernames}-form--new`]: this.isNew,
        [`${Services.sendernames}-form--${this.proxy.type}`]: true
      }
    },
    [
      renderName.call(this, h),
      renderModerationField.call(this, h),
      renderTypeField.call(this, h),
      renderCommentField.call(this, h),
      renderWarning.call(this, h),
      renderExampleComment.call(this, h),
      renderAdditional.call(this, h),
      renderRequisites.call(this, h),
      renderCommentFromAdmin.call(this, h)
    ]
  )
}
